import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { blueLightCardImages } from '../Theming/images';
import { useHistory } from 'react-router-dom';
import {
  ANALYTICS_ACTION_LABELS,
  ANALYTICS_PAGE_NAMES,
  trackActivationActionEvent,
  useIsIPad,
} from '../Common/Analytics/utils';
import { usePageViewTracking, useQuery } from '../Common/Hooks';
import { getAnalytics, logEvent } from 'firebase/analytics';
import app from '../Firebase';
import { Path } from '../Routing';
import Header from './components/Header';

const Container = styled.div<{ isIPad?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  width: 100%;
  margin: 0 auto;
  overflow-y: auto;
  overflow-x: hidden;
`;

const MainContent = styled.div<{ isIpad?: boolean }>`
  padding: ${({ isIpad }) => (isIpad ? '200px 200px 0' : '80px 24px 0')};

  & > p {
    font-family: 'Museo Sans', sans-serif;
    font-weight: 400;
    font-size: ${({ isIpad }) => (isIpad ? '26px' : '20px')};
    line-height: 100%;
    letter-spacing: 0%;
    text-align: center;
    margin: 0;
    padding: 4px 0;
  }

  @media (max-height: 670px) {
    padding: 60px 24px 0;

    & > p {
      font-size: 16px;
      line-height: 120%;
      padding: 2px 0;
    }
  }
`;

const AlwaysAllowText = styled.span<{ isIpad?: boolean }>`
  font-family: 'Museo Sans', sans-serif;
  font-weight: 800;
  font-size: ${({ isIpad }) => (isIpad ? '26px' : '20px')};
  line-height: 100%;
  letter-spacing: 0%;

  @media (max-height: 670px) {
    font-size: 16px;
    line-height: 120%;
  }
`;

const LighterText = styled.p`
  font-family: 'Museo Sans', sans-serif;
  font-weight: 300;
  font-size: inherit;
  line-height: inherit;
  letter-spacing: inherit;
  text-align: inherit;
  margin: 0;
  padding: 4px 0;
`;

export const BoldText = styled.p`
  font-family: 'Museo Sans', sans-serif;
  font-weight: 900;
  font-size: inherit;
  line-height: inherit;
  letter-spacing: inherit;
  text-align: inherit;
  margin: 0;
  padding: 4px 0;
`;

const ImagesContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding-top: 24px;
  padding-bottom: 24px;
  max-width: 600px;
  width: 100%;
  margin-top: 50px;
  background: white;
  z-index: 1;
`;

const AlwaysAllowImage = styled.img`
  width: 90%;
  height: auto;
  display: block;

  @media (max-height: 670px) {
    width: 70%;
    max-height: 80px;
    object-fit: contain;
  }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-20px);
  }
  60% {
    transform: translateY(-10px);
  }
`;

const StyledImage = styled.img`
  position: absolute;
  top: 20px;
  right: 50px;
  animation: ${bounce} 2s infinite;
  width: 170px;
  height: 60px;
  z-index: 10;
  margin-bottom: 20px;
`;

interface Message {
  data: string & { type: string; givenPermission?: boolean };
}

const BlueLightCardPluginActivationAlwaysAllow = () => {
  const history = useHistory();
  const { additional_permissions_page } = ANALYTICS_PAGE_NAMES;
  const { activation_permissions } = ANALYTICS_ACTION_LABELS;
  usePageViewTracking(additional_permissions_page, 'BlueLightCard');
  const query = useQuery();

  const isIPad = useIsIPad();

  useEffect(() => {
    const analytics = getAnalytics(app);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const messageHandler = (message: Message) => {
      if (message && message.data === '') {
        window.location.reload();
      } else if (message) {
        switch (message.data?.type) {
          case 'PluginEnabled': {
            const permissionType = message.data?.givenPermission
              ? 'full'
              : 'partial';

            // Mark that user has activated the plugin at least once
            localStorage.setItem('blc_plugin_activated', 'true');
            // Store the permission type they granted
            localStorage.setItem('blc_permission_type', permissionType);

            trackActivationActionEvent(
              query,
              'BlueLightCard',
              activation_permissions,
              additional_permissions_page,
              {
                permissions: message.data?.givenPermission ? 'Full' : 'Partial',
              }
            );
            logEvent(analytics, 'activation_permissions', {
              permissions: message.data?.givenPermission ? 'Full' : 'Partial',
              partner: 'BlueLightCard',
            });

            if (message.data?.givenPermission) {
              history.push({
                pathname: Path.BlueLightCardPluginActivationStartShopping,
                search: location.search,
              });
            } else {
              history.push({
                pathname: Path.BlueLightCardPluginActivationPartialPermissions,
                search: location.search,
              });
            }
            break;
          }
        }
      }
    };

    window.addEventListener('message', messageHandler);

    return () => window.removeEventListener('message', messageHandler);
  }, [history]);
  return (
    <Container isIPad={isIPad}>
      <StyledImage src={blueLightCardImages.safari_tap_review.default} />

      <Header
        showBrands
        height={isIPad ? 220 : 180}
        starTopPosition={'95%'}
        headingMarginTop={40}
        heading={
          <>
            <LighterText>You are almost there.</LighterText>
            <BoldText> Tap Review above to finish.</BoldText>
          </>
        }
      />
      <MainContent isIpad={isIPad}>
        <p>When prompted, select:</p>
        <p>
          <AlwaysAllowText isIpad={isIPad} as="span">
            1. &lsquo;Always Allow&rsquo;
          </AlwaysAllowText>
        </p>
        <p>
          <AlwaysAllowText isIpad={isIPad} as="span">
            2. &lsquo;Always Allow on Every Website&rsquo;
          </AlwaysAllowText>
        </p>
        <p>Otherwise we won&apos;t be able to</p>
        <p>match codes to your shopping.</p>
        <ImagesContainer>
          <AlwaysAllowImage
            src={blueLightCardImages.safari_always_allow.default}
            alt="Safari Always Allow"
          />
          <AlwaysAllowImage
            src={blueLightCardImages.safari_always_allow_everytime.default}
            alt="Safari Always Allow Every Time"
          />
        </ImagesContainer>
      </MainContent>
    </Container>
  );
};

export default BlueLightCardPluginActivationAlwaysAllow;
